import {
  AnimationClip,
  Material,
  Object3D,
  PMREMGenerator,
  RepeatWrapping,
  sRGBEncoding,
  Texture,
  TextureLoader,
  UnsignedByteType
} from 'three'
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import globalAssetsManifest from '@/components/configs/global-assets-manifest.json'
import CoinDefinition from '@/shared/common/coin-definition'

import globalRenderer from '@/shared/misc/global-renderer'
import Denomination from './common/denomination'
import Action from './common/action'
import debug from 'debug'
export interface LoadingItem {
  key: string
  url: string
  source?: string
  loadOnInit?: boolean
  size?: number
  onlyPreload?: boolean
}

const textureLoader = new TextureLoader()
const gltfLoader = new GLTFLoader()
const exrLoader = new EXRLoader()

class AssetsSystem {
  #pmremGenerator: PMREMGenerator = {} as PMREMGenerator
  #textureLibrary: Map<string, Texture> = new Map<string, Texture>()
  #modelLibrary: Map<string, Object3D> = new Map<string, Object3D>()
  #animationsLibrary: Map<string, AnimationClip[]> = new Map<string, AnimationClip[]>()
  #materialLibrary: Map<string, Material> = new Map<string, Material>()
  #loadedCoins: Map<Denomination, boolean> = new Map<Denomination, boolean>()
  #optionalLoadingItems: LoadingItem[] = []
  onEnvMapUpdated: Action<Texture> = new Action<Texture>()
  #namespaceWeakMap: WeakMap<any, string> = new WeakMap()

  async init() {
    this.#pmremGenerator = new PMREMGenerator(globalRenderer.getRenderer())
    this.#pmremGenerator.compileEquirectangularShader()

    //TODO: Only load coins used initital
    /*const coinManifest: LoadingItem[] = []
    coinDefinitions
      .filter(x => x.loadOnStart)
      .forEach((coinDefinition: CoinDefinition) => {
        this.addCoinToManifest(coinManifest, coinDefinition)
      })

    const preloadComponentImages = [
      { id: 'parallax_background', path: require('@/features/shared/components/images/parallax_background.jpg') },
      { id: 'parallax_foreground', path: require('@/features/shared/components/images/parallax_foreground.png') },
      { id: 'parallax_middle', path: require('@/features/shared/components/images/parallax_middle.png') },
      { id: 'parallax_flags', path: require('@/features/shared/components/images/flags.png') },
      { id: 'tjing_button_up', path: require('@/features/control-panel/images/tjing-button.png') },
      { id: 'tjing_button_down', path: require('@/features/control-panel/images/tjing-button-down.png') },
      { id: 'card_vault', path: require('@/features/control-panel/images/card_vault.jpg') }
    ]

    const cssImages = preloadComponentImages.map(obj => {
      return {
        key: obj.id,
        url: obj.path,
        source: '',
        loadOnInit: true,
        onlyPreload: true
      }
    })

    const allAssets: LoadingItem[] = [...(globalAssetsManifest as LoadingItem[]), ...coinManifest, ...cssImages]
    */
    const allAssets: LoadingItem[] = [...(globalAssetsManifest as LoadingItem[])]

    return this.loadInitialManifest(allAssets)
  }

  private addCoinToManifest(coinManifest: LoadingItem[], coinDefinition: CoinDefinition) {
    if (coinManifest.find(x => x.key === coinDefinition.modelName) === undefined) {
      coinManifest.push({
        key: coinDefinition.modelName,
        url: './models/coins/' + coinDefinition.modelName
      })
    }

    coinManifest.push({
      key: coinDefinition.normalMap,
      url: './textures/coins/' + coinDefinition.normalMap
    })

    coinManifest.push({
      key: coinDefinition.diffuseMap,
      url: './textures/coins/' + coinDefinition.diffuseMap
    })
  }

  loadSingleCoin(coinDefinition: CoinDefinition) {
    if (this.isCoinLoaded(coinDefinition.denomination)) {
      return Promise.resolve()
    }

    const coinManifest: LoadingItem[] = []
    this.addCoinToManifest(coinManifest, coinDefinition)
    return new Promise<void>((resolve, reject) => {
      this.loadManifest(coinManifest, 'coins').then(
        () => {
          this.#loadedCoins.set(coinDefinition.denomination, true)
          resolve()
        },
        () => {
          reject()
        }
      )
    })
  }

  isCoinLoaded(denomination: Denomination): boolean {
    if (this.#loadedCoins.has(denomination) && this.#loadedCoins.get(denomination)) {
      return true
    }
    return false
  }

  loadInitialManifest(allAssets: LoadingItem[]) {
    return new Promise<void>((resolve, reject) => {
      this.loadManifest(allAssets, 'global').then(
        () => {
          resolve()
        },
        () => {
          reject()
        }
      )
    })
  }

  loadManifest(manifest: LoadingItem[], namespace: string) {
    const allPromises: Promise<void>[] = manifest.map(item => {
      return new Promise<void>((resolve, reject) => {
        const onItemLoaded = () => {
          resolve()
        }
        const onItemProgress = () => {}
        const onItemError = () => {
          reject()
        }

        if (item.onlyPreload) {
          onItemLoaded()
          return
        }

        if (item.url.includes('.png') || item.url.includes('.jpg')) {
          textureLoader.load(
            item.url,
            texture => {
              texture.flipY = false
              texture.wrapS = texture.wrapT = RepeatWrapping
              texture.encoding = sRGBEncoding
              this.addTexture(item.key, texture, namespace)
              onItemLoaded()
            },
            onItemProgress,
            onItemError
          )
        } else if (item.url.includes('.gltf') || item.url.includes('.glb')) {
          gltfLoader.load(
            item.url,
            gltf => {
              this.addAnimations(item.key, gltf.animations, namespace)
              this.addModel(item.key, gltf.scene as Object3D, namespace)
              onItemLoaded()
            },
            onItemProgress,
            onItemError
          )
        } else if (item.url.includes('.exr')) {
          exrLoader.setDataType(UnsignedByteType).load(
            item.url,
            (texture: Texture) => {
              texture.flipY = false
              //texture.encoding = sRGBEncoding
              const exrCubeRenderTarget = this.#pmremGenerator.fromEquirectangular(texture)
              this.addTexture(item.key, exrCubeRenderTarget.texture, namespace)

              texture.dispose()
              onItemLoaded()
            },
            onItemProgress,
            onItemError
          )
        }
      })
    })

    return Promise.all(allPromises).then(
      () => {},
      error => {
        console.error('Could not load all textures:', error)
      }
    )
  }

  unloadManifest(manifest: LoadingItem[]) {
    manifest.forEach((item: LoadingItem) => {
      this.removeTexture(item.key)
      this.removeModel(item.key)
    })
  }

  unloadNamespace(namespace: string) {
    //remove textures
    const removeTextureKeys: string[] = []
    this.#textureLibrary.forEach((value, key) => {
      if (this.#namespaceWeakMap.has(value) && this.#namespaceWeakMap.get(value) === namespace) {
        removeTextureKeys.push(key)
        this.#namespaceWeakMap.delete(value)
      }
    })
    removeTextureKeys.forEach(key => {
      this.removeTexture(key)
    })

    //remove models
    const removeModelKeys: string[] = []
    this.#modelLibrary.forEach((value, key) => {
      if (this.#namespaceWeakMap.has(value) && this.#namespaceWeakMap.get(value) === namespace) {
        removeModelKeys.push(key)
        this.#namespaceWeakMap.delete(value)
      }
    })
    removeModelKeys.forEach(key => {
      this.removeModel(key)
    })

    //remove animations
    const removeAnimationsKeys: string[] = []
    this.#animationsLibrary.forEach((value, key) => {
      if (this.#namespaceWeakMap.has(value) && this.#namespaceWeakMap.get(value) === namespace) {
        removeAnimationsKeys.push(key)
        this.#namespaceWeakMap.delete(value)
      }
    })
    removeAnimationsKeys.forEach(key => {
      this.removeAnimations(key)
    })

    //remove materials
    const removeMaterialsKeys: string[] = []
    this.#materialLibrary.forEach((value, key) => {
      if (this.#namespaceWeakMap.has(value) && this.#namespaceWeakMap.get(value) === namespace) {
        removeMaterialsKeys.push(key)
        this.#namespaceWeakMap.delete(value)
      }
    })
    removeMaterialsKeys.forEach(key => {
      this.removeModel(key)
    })
  }

  addTexture(key: string, texture: Texture, namespace: string) {
    if (this.#textureLibrary.has(key)) {
      return
    } else {
      this.#namespaceWeakMap.set(texture, namespace)
      this.#textureLibrary.set(key, texture)
    }
  }

  getTexture(key: string): Texture | null {
    if (this.#textureLibrary.has(key)) {
      const texture = this.#textureLibrary.get(key)

      if (texture) {
        return texture
      } else {
        return null
      }
    } else {
      return null
    }
  }

  getTextureAsync(key: string, namespace = 'global'): Promise<Texture> {
    return new Promise((resolve, reject) => {
      const existingTexture = this.getTexture(key)
      if (existingTexture) {
        resolve(existingTexture)
      } else {
        const optionalAsset = this.#optionalLoadingItems.find(item => item.key === key)
        if (optionalAsset) {
          textureLoader.load(optionalAsset.url, texture => {
            texture.flipY = false
            texture.wrapS = texture.wrapT = RepeatWrapping
            texture.encoding = sRGBEncoding
            this.addTexture(optionalAsset.key, texture, namespace)
            resolve(texture)
          })
        } else {
          console.warn('opn', key, this.#optionalLoadingItems)
          debug('optional asset does not exist: ' + key)
          reject()
        }
      }
    })
  }

  removeTexture(key: string) {
    if (this.#textureLibrary.has(key)) {
      //TODO: unload/dispose
      if (this.#textureLibrary.get(key)) {
        this.#textureLibrary.get(key)!.dispose()
      }
      this.#textureLibrary.delete(key)
    }
  }

  addModel(key: string, model: Object3D, namespace: string) {
    model.userData.enableBloom = false
    //model.userData.hideFromBloom = true
    if (!this.#modelLibrary.has(key)) {
      this.#namespaceWeakMap.set(model, namespace)
      this.#modelLibrary.set(key, model)
    }
  }

  getModel(key: string): Object3D | undefined {
    if (this.#modelLibrary.has(key)) {
      return this.#modelLibrary.get(key)
    } else {
      return
    }
  }

  removeModel(key: string) {
    if (this.#modelLibrary.has(key)) {
      //TODO: unload/dispose
      this.#modelLibrary.delete(key)
    }
  }

  addAnimations(key: string, animations: AnimationClip[], namespace: string) {
    if (!this.#animationsLibrary.has(key)) {
      this.#animationsLibrary.set(key, animations)
      this.#namespaceWeakMap.set(animations, namespace)
    }
  }

  getAnimations(key: string): AnimationClip[] | undefined {
    if (this.#animationsLibrary.has(key)) {
      return this.#animationsLibrary.get(key)
    } else {
      return
    }
  }

  removeAnimations(key: string) {
    if (this.#animationsLibrary.has(key)) {
      //TODO: unload/dispose
      this.#animationsLibrary.delete(key)
    }
  }

  addMaterial(key: string, material: Material, namespace: string) {
    this.#namespaceWeakMap.set(material, namespace)
    this.#materialLibrary.set(key, material)
  }

  getMaterial(key: string): Material | undefined {
    if (this.#materialLibrary.has(key)) {
      return this.#materialLibrary.get(key)
    } else {
      return
    }
  }

  removeMaterial(key: string) {
    if (this.#materialLibrary.has(key)) {
      //TODO: unload/dispose
      this.#materialLibrary.delete(key)
    }
  }
}
export default new AssetsSystem()
